import { ApiService } from "./ApiService";
import { IUploadApiService, ContentRange } from "./IUploadApiService";
import { SimpleFileModel } from "../models/SimpleFileModel";
import { FileUploadResponseModel } from "../models/FileUploadResponseModel";
import { ApiRequestOptions } from "./IApiService";
import axios, { CancelTokenSource } from "axios";

export class UploadApiService extends ApiService implements IUploadApiService {
    constructor(baseUrl: string, language?: string) {
        super(baseUrl, language);
    }

    public initResumableUpload(
        folderId: string,
        fileName: string,
        fileSize: number,
        comments: string
    ): Promise<FileUploadResponseModel> {
        const data = new FormData();
        data.append("name", fileName);
        data.append("size", fileSize.toString());
        data.append("comments", comments);

        return this.post<FileUploadResponseModel>(
            `folders/${folderId}/files?uploadType=resumable`,
            data
        );
    }

    public continueResumableUpload(
        folderId: string,
        fileId: string,
        data: Blob,
        contentRange: ContentRange,
        fileHash?: string,
        onProgress?: (e: ProgressEvent) => void,
        cancelTokenSource?: CancelTokenSource
    ): Promise<void | SimpleFileModel> {
        const headers: { "Content-Range": string; "X-File-Hash"?: string } = {
            "Content-Range": makeContentRangeHeaderValue(contentRange),
        };
        if (fileHash) {
            headers["X-File-Hash"] = fileHash;
        }
        const options: ApiRequestOptions = {
            onUploadProgress: onProgress,
            cancelToken: cancelTokenSource?.token,
        };

        if (isLastChunk(contentRange)) {
            return this.put<SimpleFileModel>(
                `folders/${folderId}/files?uploadType=resumable&fileId=${fileId}`,
                data,
                headers,
                options
            );
        } else {
            // Non-final chunk uploads should return a 308 status on success
            options.validateStatus = (status: number) => status === 308;
            return this.put<void>(
                `folders/${folderId}/files?uploadType=resumable&fileId=${fileId}`,
                data,
                headers,
                options
            );
        }
    }
}

function makeContentRangeHeaderValue(value: ContentRange): string {
    // When uploading an empty file Content-Range: bytes 0-0/0 is an invalid header. bytes */0 is a valid header
    return value.totalSize === 0 ? `bytes */0` : `bytes ${value.startOffset}-${value.endOffset}/${value.totalSize}`;
}

function isLastChunk(contentRange: ContentRange): boolean {
    return contentRange.totalSize === 0 || contentRange.endOffset === contentRange.totalSize - 1;
}
